/**
 * Dexter ships as two web apps from one codebase: Data Collection at
 * dexter.hammerheadtech.co and Orca (agents) at orca.hammerheadtech.co. They
 * share one login, one tenant and admin backend, and one set of product
 * entitlements. VITE_APP_SURFACE picks the app at build time; unset builds
 * keep every module, as before the split.
 */
export type AppSurface = 'collect' | 'orca' | 'all';

export type ProductKey =
  | 'data_collection'
  | 'red_team'
  | 'appbench'
  | 'physical_ai'
  | 'promptbench'
  | 'study_scheduling'
  | 'agents';

export const SURFACE_PRODUCTS: Record<Exclude<AppSurface, 'all'>, readonly ProductKey[]> = {
  collect: ['data_collection', 'study_scheduling'],
  orca: ['agents', 'red_team', 'promptbench', 'appbench', 'physical_ai'],
};

export const SURFACE_NAMES: Record<Exclude<AppSurface, 'all'>, string> = {
  collect: 'Dexter',
  orca: 'Orca',
};

export const SURFACE_URLS: Record<Exclude<AppSurface, 'all'>, string> = {
  collect: 'https://dexter.hammerheadtech.co',
  orca: 'https://orca.hammerheadtech.co',
};

export function parseSurface(value: string | undefined): AppSurface {
  return value === 'collect' || value === 'orca' ? value : 'all';
}

export const appSurface: AppSurface = parseSurface(import.meta.env.VITE_APP_SURFACE);
export const appName = appSurface === 'orca' ? SURFACE_NAMES.orca : SURFACE_NAMES.collect;

/** True when this build includes the product's pages. */
export function surfaceIncludes(product: ProductKey, surface: AppSurface = appSurface): boolean {
  return surface === 'all' || SURFACE_PRODUCTS[surface].includes(product);
}

export function otherSurface(surface: AppSurface = appSurface): Exclude<AppSurface, 'all'> | null {
  return surface === 'collect' ? 'orca' : surface === 'orca' ? 'collect' : null;
}

export type SurfaceAccess = 'allowed' | 'no_products' | 'role_blocked';

type ProductMap = Partial<Record<ProductKey, boolean>>;

/**
 * Whether a user may use this app. Super admins use both. Recruiters and
 * schedulers only work in Study Scheduling. Participants and QA reviewers use
 * Orca only for Red Team campaigns.
 */
export function surfaceAccessFor(
  user: { role?: string; products?: ProductMap } | null | undefined,
  surface: AppSurface = appSurface,
): SurfaceAccess {
  if (!user || surface === 'all' || user.role === 'super_admin') return 'allowed';
  const products = user.products ?? {};
  if (surface === 'orca') {
    if (user.role === 'recruiter' || user.role === 'scheduler') return 'role_blocked';
    if (user.role === 'participant' || user.role === 'qa') return products.red_team ? 'allowed' : 'role_blocked';
  }
  return SURFACE_PRODUCTS[surface].some((product) => products[product] === true) ? 'allowed' : 'no_products';
}

const ALL_PRODUCTS: readonly ProductKey[] = [...SURFACE_PRODUCTS.collect, ...SURFACE_PRODUCTS.orca];

/**
 * Turns off products this app doesn't ship, so every existing module check
 * hides them. Products this app does ship keep their value, including
 * "not reported", which several checks treat as on.
 */
export function maskProducts<T extends ProductMap>(products: T | undefined, surface: AppSurface = appSurface): T {
  if (surface === 'all') return (products ?? {}) as T;
  const masked: ProductMap = { ...(products ?? {}) };
  for (const key of ALL_PRODUCTS) if (!surfaceIncludes(key, surface)) masked[key] = false;
  return masked as T;
}

/** Products the user's tenant has in the other app, for the "switch app" link. */
export function hasOtherSurfaceProducts(products: ProductMap | undefined, surface: AppSurface = appSurface): boolean {
  const other = otherSurface(surface);
  return Boolean(other && products && SURFACE_PRODUCTS[other].some((product) => products[product] === true));
}
